export type Presence = "engaged" | "attending" | "away" | "unknown";
export type ActivityMode = "interactive_mac" | "passive_mac" | "away_from_mac" | "mixed";

export interface TimerContext {
  tagIds?: string[];
  activityMode?: ActivityMode;
}

export interface RunningTimer {
  id: string;
  description: string;
  startedAt: number;
  tagIds: string[];
  activityMode: ActivityMode;
  lastReviewedAt?: number;
  snoozedUntil?: number;
}

export interface CompletedTimerRevision {
  description: string;
  startedAt: number;
  endedAt: number;
  tagIds: string[];
  activityMode: ActivityMode;
  supersededAt: number;
}

export interface CompletedTimer extends RunningTimer {
  endedAt: number;
  revisions?: CompletedTimerRevision[];
}

export interface CompletedTimerEdit {
  description: string;
  startedAt: number;
  endedAt: number;
  tagIds: string[];
}

export interface ResumeTimerResult {
  running: RunningTimer;
  completed: CompletedTimer | null;
  resumed: boolean;
}

export interface TagDefinition {
  id: string;
  label: string;
  color: string;
  isFavorite: boolean;
  defaultActivityMode: ActivityMode;
  createdAt: number;
  archivedAt?: number;
}

export interface ReviewContext {
  presence: Presence;
  pageVisible: boolean;
  previousPresence?: Presence;
  returnedAfterAwayMs?: number;
  engagedForMs?: number;
  quietHours?: boolean;
}

export type ReviewReason = "overdue" | "returned" | "mode-contradiction";
export type QuietReason = "active-flow" | "attending" | "away" | "quiet-hours" | "uncertain";

export type ReviewDecision =
  | { kind: "not-due"; dueAt: number }
  | { kind: "quiet"; reason: QuietReason; dueAt: number }
  | { kind: "prompt"; reason: ReviewReason; dueAt: number };

export const REVIEW_AFTER_MS = 60 * 60 * 1000;
export const CONTRADICTION_AFTER_MS = 3 * 60 * 1000;
export const RETURN_BOUNDARY_MS = 10 * 60 * 1000;

const cleanTagIds = (tagIds: string[] | undefined): string[] => [
  ...new Set((tagIds ?? []).filter((tagId) => typeof tagId === "string" && tagId.trim()).map((tagId) => tagId.trim())),
];

const cleanDescription = (description: string): string => description.trim().replace(/\s+/g, " ");
const cleanTagLabel = (label: string): string => label.trim().replace(/\s+/g, " ").slice(0, 40);

export function startTimer(description: string, at = Date.now(), context: TimerContext = {}): RunningTimer {
  const clean = cleanDescription(description);
  if (!clean) throw new Error("Describe what you are doing first.");
  return {
    id: crypto.randomUUID(),
    description: clean,
    startedAt: at,
    tagIds: cleanTagIds(context.tagIds),
    activityMode: context.activityMode ?? "mixed",
  };
}

export function finishTimer(timer: RunningTimer, at = Date.now()): CompletedTimer {
  if (at < timer.startedAt) throw new Error("A timer cannot finish before it starts.");
  return { ...timer, endedAt: at };
}

export function retagCompletedTimer(timer: CompletedTimer, tagIds: string[]): CompletedTimer {
  return { ...timer, tagIds: cleanTagIds(tagIds) };
}

export function reviseCompletedTimer(timer: CompletedTimer, edit: CompletedTimerEdit, at = Date.now()): CompletedTimer {
  const description = cleanDescription(edit.description);
  if (!description) throw new Error("Give this entry a title.");
  if (description.length > 180) throw new Error("Keep the title under 180 characters.");
  if (!Number.isSafeInteger(edit.startedAt) || edit.startedAt < 0) throw new Error("Choose a valid start time.");
  if (!Number.isSafeInteger(edit.endedAt) || edit.endedAt < edit.startedAt) throw new Error("End time must be after start time.");
  if (!Number.isSafeInteger(at) || at < 0) throw new Error("Choose a valid revision time.");
  const tagIds = cleanTagIds(edit.tagIds);
  const unchanged = description === timer.description &&
    edit.startedAt === timer.startedAt &&
    edit.endedAt === timer.endedAt &&
    tagIds.length === timer.tagIds.length &&
    tagIds.every((tagId) => timer.tagIds.includes(tagId));
  if (unchanged) return timer;
  const revision: CompletedTimerRevision = {
    description: timer.description,
    startedAt: timer.startedAt,
    endedAt: timer.endedAt,
    tagIds: [...timer.tagIds],
    activityMode: timer.activityMode,
    supersededAt: at,
  };
  return {
    ...timer,
    description,
    startedAt: edit.startedAt,
    endedAt: edit.endedAt,
    tagIds,
    revisions: [...(timer.revisions ?? []), revision].slice(-20),
  };
}

export function switchTimer(
  timer: RunningTimer,
  description: string,
  at = Date.now(),
  context: TimerContext = {},
): { completed: CompletedTimer; running: RunningTimer } {
  return { completed: finishTimer(timer, at), running: startTimer(description, at, context) };
}

export function resumeCompletedTimer(
  source: CompletedTimer,
  running: RunningTimer | null,
  at = Date.now(),
): ResumeTimerResult {
  const sameTags = running !== null &&
    running.tagIds.length === source.tagIds.length &&
    running.tagIds.every((tagId) => source.tagIds.includes(tagId));
  if (
    running && running.description === source.description &&
    running.activityMode === source.activityMode && sameTags
  ) return { running, completed: null, resumed: false };

  const context = { tagIds: source.tagIds, activityMode: source.activityMode };
  if (!running) return { running: startTimer(source.description, at, context), completed: null, resumed: true };
  const switched = switchTimer(running, source.description, at, context);
  return { ...switched, resumed: true };
}

export function createTag(
  label: string,
  color: string,
  defaultActivityMode: ActivityMode = "mixed",
  at = Date.now(),
): TagDefinition {
  const clean = cleanTagLabel(label);
  if (!clean) throw new Error("Name this tag first.");
  if (!/^#[0-9a-f]{6}$/i.test(color)) throw new Error("Choose a valid tag color.");
  return {
    id: crypto.randomUUID(),
    label: clean.slice(0, 40),
    color: color.toLowerCase(),
    isFavorite: true,
    defaultActivityMode,
    createdAt: at,
  };
}

export function renameTag(tag: TagDefinition, label: string): TagDefinition {
  const clean = cleanTagLabel(label);
  if (!clean) throw new Error("Name this tag first.");
  if (clean === tag.label) return tag;
  return { ...tag, label: clean };
}

export function archiveTag(tag: TagDefinition, at = Date.now()): TagDefinition {
  return { ...tag, archivedAt: at, isFavorite: false };
}

export function normalizeTimer<T extends { id: string; description: string; startedAt: number }>(timer: T): T & RunningTimer {
  const candidate = timer as T & Partial<RunningTimer>;
  const modes: ActivityMode[] = ["interactive_mac", "passive_mac", "away_from_mac", "mixed"];
  return {
    ...timer,
    tagIds: cleanTagIds(candidate.tagIds),
    activityMode: modes.includes(candidate.activityMode as ActivityMode) ? candidate.activityMode as ActivityMode : "mixed",
  };
}

export function reviewDecision(
  timer: RunningTimer,
  context: ReviewContext | Presence,
  now = Date.now(),
  reviewAfterMs = REVIEW_AFTER_MS,
): ReviewDecision {
  const detail: ReviewContext = typeof context === "string"
    ? { presence: context, pageVisible: false }
    : context;
  const baseline = Math.max(timer.startedAt, timer.lastReviewedAt ?? 0);
  const dueAt = Math.max(baseline + reviewAfterMs, timer.snoozedUntil ?? 0);
  const contradiction = timer.activityMode === "away_from_mac" &&
    detail.presence === "engaged" &&
    (detail.engagedForMs ?? 0) >= CONTRADICTION_AFTER_MS &&
    now - timer.startedAt >= CONTRADICTION_AFTER_MS;

  if (!contradiction && now < dueAt) return { kind: "not-due", dueAt };
  if (detail.quietHours) return { kind: "quiet", reason: "quiet-hours", dueAt };
  if (contradiction) return { kind: "prompt", reason: "mode-contradiction", dueAt };
  if (
    detail.previousPresence === "away" &&
    detail.presence === "engaged" &&
    (detail.returnedAfterAwayMs ?? 0) >= RETURN_BOUNDARY_MS
  ) return { kind: "prompt", reason: "returned", dueAt };
  if (detail.pageVisible) return { kind: "prompt", reason: "overdue", dueAt };
  if (detail.presence === "engaged") return { kind: "quiet", reason: "active-flow", dueAt };
  if (detail.presence === "attending") return { kind: "quiet", reason: "attending", dueAt };
  if (detail.presence === "away") return { kind: "quiet", reason: "away", dueAt };
  return { kind: "prompt", reason: "overdue", dueAt };
}

export function reviewExplanation(reason: ReviewReason, elapsedMs: number, awayMs = 0): string {
  if (reason === "returned") return `You returned after ${Math.max(1, Math.round(awayMs / 60_000))} minutes away.`;
  if (reason === "mode-contradiction") return "This entry expects you away from the Mac, but sustained Mac activity was detected.";
  return `This timer has been running for ${Math.max(1, Math.round(elapsedMs / 60_000))} minutes.`;
}
